// CHAT-THREAD-AGENTS: the agents taking part in a reply thread, as small chips in the thread
// header (the server's `threads.agents?root=` `participating`). Each chip has a hover "Leave
// thread" icon (visible on touch): the agent is muted there — replies that name nobody no longer
// wake it; an @mention brings it back.
import { Badge, Button, toast, workerName } from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BotIcon, LogOutIcon } from "lucide-react";
import { chatKeys, threadAgentsQuery } from "./chat-queries";
import { DeAvatar, DeHoverCard, ringHint, useDePresence } from "./de-presence";
import type { ChatContext } from "./use-chat";

export function ThreadAgentChips({
  chat,
  threadId,
  root,
}: {
  chat: ChatContext;
  threadId: string;
  root: string;
}) {
  const { api, co } = chat;
  const queryClient = useQueryClient();
  const hasAgents = chat.mentionables.some((m) => m.kind === "agent");
  const q = useQuery({
    ...threadAgentsQuery(api, co, threadId, root),
    enabled: hasAgents && threadId !== "" && chat.meId !== null,
  });
  const leave = useMutation({
    mutationFn: (agent: { id: string; name: string }) =>
      api.client.threads.leaveAgent({
        params: { co, id: threadId, mid: root, membership_id: agent.id },
      }),
    onSuccess: (_r, agent) => {
      void queryClient.invalidateQueries({ queryKey: chatKeys.agents(co, threadId) });
      toast.add({ title: `${agent.name} left this thread`, type: "success" });
    },
    onError: () => toast.add({ title: "Could not remove the agent", type: "error" }),
  });
  const rows = (q.data?.data ?? []).filter((r) => r.participating);
  const presence = useDePresence(
    api,
    co,
    rows.some((r) => r.agent_kind === "digital_employee"),
  );
  if (rows.length === 0 || chat.readOnly) return null;
  return (
    <ul
      data-slot="thread-agents"
      aria-label="Agents in this thread"
      className="flex items-center gap-1"
    >
      {rows.map((r) => (
        <li key={r.membership_id} className="group/agent relative flex items-center">
          <Badge variant="outline" className="h-6 gap-1 pr-1 text-xs" data-slot="thread-agent">
            {/* CHAT-PRESENCE: a DE's chip carries its ring and opens its card. */}
            {r.agent_kind === "digital_employee" ? (
              <DeHoverCard
                api={api}
                co={co}
                slug={chat.company.slug}
                de={{ id: r.membership_id, name: r.name }}
                presence={presence.get(r.membership_id)}
                canAct={chat.meId !== null && !chat.readOnly}
              >
                <DeAvatar
                  name={r.name}
                  state={presence.get(r.membership_id)?.state}
                  hint={ringHint(presence.get(r.membership_id))}
                />
              </DeHoverCard>
            ) : (
              <BotIcon aria-hidden="true" className="size-3" />
            )}
            <span className="max-w-24 truncate">{workerName(r.name)}</span>
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label={`Leave thread: stop ${r.name} answering here`}
              title="Leave thread"
              disabled={leave.isPending}
              onClick={() => leave.mutate({ id: r.membership_id, name: r.name })}
              className="size-4 opacity-0 transition-opacity group-hover/agent:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100"
            >
              <LogOutIcon aria-hidden="true" className="size-3" />
            </Button>
          </Badge>
        </li>
      ))}
    </ul>
  );
}
